import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import showtoast from '../../../../../../../../util/toast';
import WhatsappFlowModelMC from '../../../../../../../models/WhatsappFlowModelMC';

/**
 * Default message config for trigger_whatsapp_flow tool.
 * Persists body/footer/cta on the WhatsappFlow record (does not touch fields_mapping).
 */
const useTriggerWhatsappFlowToolConfig = ({ selectedFlow }) => {
  const { t } = useTranslation('WhatsappFlowsScreenMC');

  const methods = useForm({
    defaultValues: {
      defaultFlowCta: '',
      defaultBodyText: '',
      defaultFooterText: ''
    }
  });

  const [defaultFlowCta, setDefaultFlowCta] = useState('');
  const [defaultBodyText, setDefaultBodyText] = useState('');
  const [defaultFooterText, setDefaultFooterText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!selectedFlow) {
      setDefaultFlowCta('');
      setDefaultBodyText('');
      setDefaultFooterText('');
      methods.reset({
        defaultFlowCta: '',
        defaultBodyText: '',
        defaultFooterText: ''
      });
      return;
    }

    const nextCta = selectedFlow.default_flow_cta || '';
    const nextBody = selectedFlow.default_body_text || '';
    const nextFooter = selectedFlow.default_footer_text || '';

    setDefaultFlowCta(nextCta);
    setDefaultBodyText(nextBody);
    setDefaultFooterText(nextFooter);
    methods.reset({
      defaultFlowCta: nextCta,
      defaultBodyText: nextBody,
      defaultFooterText: nextFooter
    });
  }, [selectedFlow, methods.reset]);

  const saveFlowConfiguration = useCallback(async () => {
    if (!selectedFlow?.meta_flow_id) return false;

    setSaving(true);
    try {
      await WhatsappFlowModelMC.put({
        id: selectedFlow.meta_flow_id,
        whatsapp_flow: {
          default_flow_cta: defaultFlowCta,
          default_body_text: defaultBodyText,
          default_footer_text: defaultFooterText
        }
      });

      selectedFlow.default_flow_cta = defaultFlowCta;
      selectedFlow.default_body_text = defaultBodyText;
      selectedFlow.default_footer_text = defaultFooterText;

      return true;
    } catch (_e) {
      showtoast(t('mapping_saved_error'));
      return false;
    } finally {
      setSaving(false);
    }
  }, [
    defaultBodyText,
    defaultFlowCta,
    defaultFooterText,
    selectedFlow,
    t
  ]);

  return {
    methods,
    flowConfigT: t,
    saving,
    defaultBodyText,
    setDefaultBodyText,
    defaultFooterText,
    setDefaultFooterText,
    defaultFlowCta,
    setDefaultFlowCta,
    saveFlowConfiguration
  };
};

export default useTriggerWhatsappFlowToolConfig;
